import React from 'react';
import {translations} from 'presentation/translations-new';
import {Button, Stack, Typography, useTheme} from '@mui/material';
import Icon from 'presentation/icons';
import {redirectToWhatsApp, makePhoneCall, sendEmail} from 'presentation/utils';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {Drawer} from '../Drawer';
import {useConciergeDrawerStore} from './store';
import {IconIllustration} from '../IconIllustration';
import type {ListInfosType} from './types';
import {StyledContainerItem, StyledDescriptionItem, StyledTitleItem} from './styles';
import {StyledGrid} from '../Grid';

const {title, descriptionInfinity, descriptionTRT, infos} =
	translations['pt-br'].privateFlow.conciergeDrawer;

export const ConciergeDrawer = () => {
	const theme = useTheme();
	const isOpen = useConciergeDrawerStore(state => state.isOpen);
	const onOpen = useConciergeDrawerStore(state => state.onOpen);
	const onClose = useConciergeDrawerStore(state => state.onClose);

	const {data: healthCardData} = useGetHealthCardQuery();

	const isInfinityPlan = healthCardData?.nmPlano?.includes('INFINITY') ?? false;

	const description = isInfinityPlan ? descriptionInfinity : descriptionTRT;

	const serviceDescription = isInfinityPlan
		? infos.service.descriptionInfinity
		: infos.service.descriptionTRT;

	const mailDescription = isInfinityPlan
		? infos.mail.descriptionInfinity
		: infos.mail.descriptionTRT;

	const phoneDescription = isInfinityPlan
		? infos.phone.descriptionInfinity
		: infos.phone.descriptionTRT;

	const callcenterDescription = isInfinityPlan
		? infos.callcenter.descriptionInfinity
		: infos.callcenter.descriptionTRT;

	const whatsappDescription = isInfinityPlan
		? infos.whatsapp.descriptionInfinity
		: infos.whatsapp.descriptionTRT;

	const handleEmailClick = () => {
		sendEmail(mailDescription, infos.mail.mailTitle, infos.mail.mailMessage);
	};

	const handlePhoneClick = () => {
		makePhoneCall(phoneDescription);
	};

	const handleCallcenterClick = () => {
		makePhoneCall(callcenterDescription);
	};

	const handleWhatsAppClick = () => {
		redirectToWhatsApp(whatsappDescription, infos.whatsapp.whatsappMessage);
	};

	const listInfos: ListInfosType[] = [
		{
			title: infos.service.title,
			description: serviceDescription,
			icon: 'person-filled',
		},
		{
			title: infos.mail.title,
			description: mailDescription,
			icon: 'mail-filled',
			isLink: true,
			handleClick: handleEmailClick,
		},
		{
			title: infos.phone.title,
			description: phoneDescription,
			icon: 'phone-filled',
			isLink: true,
			handleClick: handlePhoneClick,
		},
		{
			title: infos.callcenter.title,
			description: callcenterDescription,
			icon: 'phone-filled',
			isLink: true,
			handleClick: handleCallcenterClick,
		},
		{
			title: infos.whatsapp.title,
			description: whatsappDescription,
			icon: 'whatsapp-filled',
			isLink: true,
			handleClick: handleWhatsAppClick,
		},
	];

	return (
		<Drawer.Root
			open={isOpen}
			onOpen={onOpen}
			onClose={onClose}
			width={'474px'}
			customMaxHeight={'90%'}
			disableSwipeToOpen>
			<Drawer.Header title={title} onClose={onClose} />
			<Drawer.Content>
				<Stack gap={2} mt={2}>
					<Typography variant="body2">{description}</Typography>
					<Stack>
						{listInfos.map((info, index) => {
							const descriptionSizeGrid = info.isLink ? 8 : 10;
							const iconSizeGrid = 2;
							const buttonSizeGrid = info.isLink ? 2 : 0;
							return (
								<StyledContainerItem
									key={index}
									$isLast={index === listInfos.length - 1}>
									<StyledGrid.Item
										xs={iconSizeGrid}
										sm={iconSizeGrid}
										md={iconSizeGrid}
										lg={iconSizeGrid}>
										<Stack alignItems={'center'}>
											<IconIllustration
												variant="primary"
												customIconName={info.icon}
												size="small"
											/>
										</Stack>
									</StyledGrid.Item>
									<StyledGrid.Item
										xs={descriptionSizeGrid}
										sm={descriptionSizeGrid}
										md={descriptionSizeGrid}
										lg={descriptionSizeGrid}>
										<Stack flex={1} justifyContent={'center'} height={'100%'}>
											<StyledTitleItem>{info.title}</StyledTitleItem>
											<StyledDescriptionItem
												$isLink={info.isLink}
												onClick={info.handleClick}>
												{info.description}
											</StyledDescriptionItem>
										</Stack>
									</StyledGrid.Item>
									{info.isLink ? (
										<StyledGrid.Item
											xs={buttonSizeGrid}
											sm={buttonSizeGrid}
											md={buttonSizeGrid}
											lg={buttonSizeGrid}>
											<Stack
												alignItems={'center'}
												justifyContent={'center'}
												height={'100%'}>
												<Button variant="soft" onClick={info.handleClick}>
													<Icon
														name="arrow-right"
														color={theme.palette.primary.main}
													/>
												</Button>
											</Stack>
										</StyledGrid.Item>
									) : null}
								</StyledContainerItem>
							);
						})}
					</Stack>
				</Stack>
			</Drawer.Content>
		</Drawer.Root>
	);
};
